import { useState, useMemo, useTransition, useRef, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useProducts } from "@/hooks/useProducts";
import type { SupplyProduct } from "@/hooks/useProducts";

const SUB_CATEGORIES = ["全部", "衣物", "生活用品", "寵物用品", "母嬰用品", "電子產品", "食品", "其他"];

// ---- Categorized cache helpers ----
const FILTER_CACHE_TTL = 5 * 60 * 1000;

function getFilteredCacheKey(tab: string, subCategory: string): string {
  return `shop_filtered_${tab}_${subCategory}_v1`;
}

function getFilteredCached(tab: string, subCategory: string): SupplyProduct[] | null {
  try {
    const raw = sessionStorage.getItem(getFilteredCacheKey(tab, subCategory));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data || !parsed.timestamp) return null;
    if (Date.now() - parsed.timestamp > FILTER_CACHE_TTL) {
      sessionStorage.removeItem(getFilteredCacheKey(tab, subCategory));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setFilteredCached(tab: string, subCategory: string, data: SupplyProduct[]) {
  try {
    sessionStorage.setItem(
      getFilteredCacheKey(tab, subCategory),
      JSON.stringify({ data, timestamp: Date.now() }),
    );
  } catch {
    // ignore
  }
}

// Preload images for a list of products to avoid visual flash on tab switch
function preloadProductImages(products: SupplyProduct[]) {
  products.forEach((p) => {
    if (p.imageUrl) {
      const img = new Image();
      img.src = p.imageUrl;
    }
  });
}

function ProductCard({ product }: { product: SupplyProduct }) {
  const [imgError, setImgError] = useState(false);
  const profit = product.sellingPrice - product.price;
  const progress =
    product.stockQuantity > 0
      ? Math.min(
          100,
          Math.round((product.raised / product.stockQuantity) * 100),
        )
      : 0;
  const remaining = Math.max(0, product.stockQuantity - product.raised);

  return (
    <div className="group relative bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-stone-300 transition-all">
      {/* Image area */}
      <div className="relative w-full h-48 md:h-56 bg-stone-100 flex items-center justify-center overflow-hidden">
        {product.imageUrl && !imgError ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                product.type === "trial" ? "bg-rose-100" : "bg-amber-100"
              }`}
            >
              <i
                className={`${
                  product.type === "trial"
                    ? "ri-sparkling-line text-rose-600"
                    : "ri-vip-crown-line text-amber-600"
                } text-xl`}
              />
            </div>
            <span className="font-body text-xs text-stone-400 text-center px-4">
              {product.name}
            </span>
          </div>
        )}

        {/* Zone tag */}
        <div className="absolute top-3 left-3">
          {product.type === "trial" ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-800 text-white font-body text-[10px] font-semibold">
              <i className="ri-sparkling-line" />
              體驗
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-stone-900 font-body text-[10px] font-semibold">
              <i className="ri-vip-crown-line" />
              VIP
            </span>
          )}
        </div>

        {/* Category tag */}
        {product.category && product.category !== "全部" && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-2 py-1 rounded-full bg-white/90 text-stone-600 font-body text-[10px] font-medium border border-stone-200/50 backdrop-blur-sm">
              {product.category}
            </span>
          </div>
        )}

        {/* Progress badge */}
        {product.raised > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-white/20 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all duration-700"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="font-body text-[10px] text-white font-semibold">
                {progress}%
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-body text-sm font-semibold text-stone-800 mb-1 line-clamp-1">
          {product.name}
        </h3>
        {product.description && (
          <p className="font-body text-[11px] text-stone-500 mb-3 line-clamp-1">
            {product.description}
          </p>
        )}

        {/* Price / Profit / Return rate */}
        <div className="flex items-center gap-3 mb-3">
          <div>
            <p className="font-body text-[10px] text-stone-400">認購價</p>
            <p className="font-display text-sm font-bold text-stone-800">
              NT$ {product.price.toLocaleString()}
            </p>
          </div>
          <div className="w-px h-8 bg-stone-200" />
          <div>
            <p className="font-body text-[10px] text-stone-400">銷售價</p>
            <p className="font-display text-sm font-bold text-emerald-600">
              NT$ {product.sellingPrice.toLocaleString()}
            </p>
          </div>
          <div className="ml-auto">
            <span className="font-body text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
              NT$ {profit.toLocaleString()}
            </span>
          </div>
        </div>

        <p className="font-body text-[10px] text-stone-500 mb-3">
          {product.type === "trial" ? `限購 ${remaining} 組` : `必購 ${remaining} 組`}
        </p>

        <Link
          to={product.type === "trial" ? "/subscribe-trial" : "/vip-subscribe"}
          className="block w-full text-center font-body text-xs font-semibold py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 transition-colors whitespace-nowrap"
        >
          立即認購
        </Link>
      </div>
    </div>
  );
}

const tabItems = [
  { key: "trial" as const, label: "體驗認購區" },
  { key: "vip" as const, label: "VIP 認購區" },
];

export default function Shop() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"trial" | "vip">("trial");
  const [activeSubCategory, setActiveSubCategory] = useState<string>("全部");
  const [searchQuery, setSearchQuery] = useState("");
  const [isPending, startTransition] = useTransition();
  const { products: allProducts, loading, error } = useProducts();

  // Debounce timer ref for tab/subcategory switching
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync sub-category from URL query param (e.g. ?category=女裝)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("category");
    if (cat && SUB_CATEGORIES.includes(cat)) {
      startTransition(() => {
        setActiveSubCategory(cat);
      });
    }
  }, [location.search, startTransition]);

  // ---- Filtered products with categorized cache fallback ----
  const filteredProducts = useMemo(() => {
    // If allProducts hasn't loaded yet, try to show cached data for current filter
    if (allProducts.length === 0) {
      const cached = getFilteredCached(activeTab, activeSubCategory);
      return cached || [];
    }

    let result = allProducts.filter((p) => p.type === activeTab);

    if (activeSubCategory !== "全部") {
      result = result.filter((p) => p.category === activeSubCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.tag.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q)) ||
          (p.category && p.category.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [allProducts, activeTab, activeSubCategory, searchQuery]);

  // Cache filtered results after computation
  useEffect(() => {
    if (allProducts.length > 0 && filteredProducts.length > 0) {
      setFilteredCached(activeTab, activeSubCategory, filteredProducts);
    }
  }, [filteredProducts, activeTab, activeSubCategory, allProducts.length]);

  // Preload images for the current filtered list to reduce visual flash
  useEffect(() => {
    if (filteredProducts.length > 0) {
      preloadProductImages(filteredProducts);
    }
  }, [filteredProducts]);

  const counts = useMemo(() => {
    return {
      trial: allProducts.filter((p) => p.type === "trial").length,
      vip: allProducts.filter((p) => p.type === "vip").length,
    };
  }, [allProducts]);

  const subCategoryCounts = useMemo(() => {
    const relevant = allProducts.filter((p) => p.type === activeTab);
    const map: Record<string, number> = { 全部: relevant.length };
    relevant.forEach((p) => {
      const cat = p.category || "未分類";
      map[cat] = (map[cat] || 0) + 1;
    });
    return map;
  }, [allProducts, activeTab]);

  // ---- Debounced tab handlers with useTransition ----
  const handleTabChange = useCallback(
    (tab: "trial" | "vip") => {
      if (tab === activeTab) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setActiveTab(tab);
          setActiveSubCategory("全部");
        });
      }, 80);
    },
    [activeTab, startTransition],
  );

  const handleSubCategoryChange = useCallback(
    (cat: string) => {
      if (cat === activeSubCategory) return;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setActiveSubCategory(cat);
        });
      }, 80);
    },
    [activeSubCategory, startTransition],
  );

  const clearFilters = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    startTransition(() => {
      setActiveTab("trial");
      setActiveSubCategory("全部");
      setSearchQuery("");
    });
  }, [startTransition]);

  // Skeleton only when truly no data available (no cache, no loaded products)
  const showSkeleton = loading && allProducts.length === 0 && filteredProducts.length === 0;

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-stone-900 pt-20 pb-8">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <Navbar />
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white mt-4">
            全部貨源
          </h1>
          <p className="font-body text-sm text-stone-400 mt-2">
            瀏覽體驗區與 VIP 區認購貨源，一站式查看認購價、銷售價與利潤。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-6">
        {/* Search */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
          <div className="flex items-center gap-2 w-full md:max-w-md">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                type="text"
                placeholder="搜尋貨源名稱、標籤、類別..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-full border border-stone-200 font-body text-sm text-stone-700 focus:outline-none focus:border-stone-400"
              />
            </div>
          </div>
          <p className="font-body text-xs text-stone-500">
            {filteredProducts.length} 個結果
          </p>
        </div>

        {/* Main category filter tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tabItems.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-1.5 font-body text-xs font-medium px-5 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap border ${
                activeTab === tab.key
                  ? "bg-rose-900 text-white border-rose-900 shadow-sm"
                  : tab.key === "vip"
                    ? "bg-white text-amber-600 border-amber-300 hover:border-amber-400 hover:bg-amber-50"
                    : "bg-white text-stone-600 border-stone-200 hover:border-stone-300 hover:bg-stone-50"
              } ${isPending && activeTab !== tab.key ? "opacity-60" : ""}`}
            >
              {tab.key === "vip" && (
                <i className="ri-vip-crown-line text-sm" />
              )}
              {tab.key === "trial" && (
                <i className="ri-sparkling-line text-sm" />
              )}
              {tab.label}
              <span
                className={`ml-1 font-body text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key
                    ? "bg-white/20 text-white"
                    : "bg-stone-100 text-stone-500"
                }`}
              >
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Sub-category filter bar */}
        <div className="flex flex-wrap items-center gap-1.5 mb-5 pl-1">
          <span className="font-body text-[11px] text-stone-400 mr-2">
            子類別：
          </span>
          {SUB_CATEGORIES.map((cat) => {
            const isActive = activeSubCategory === cat;
            const count = subCategoryCounts[cat] ?? 0;
            return (
              <button
                key={cat}
                onClick={() => handleSubCategoryChange(cat)}
                className={`font-body text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? "bg-rose-900 text-white border-rose-900"
                    : "bg-white text-stone-500 border-stone-200 hover:border-stone-300 hover:text-stone-700"
                } ${isPending && activeSubCategory !== cat ? "opacity-60" : ""}`}
              >
                {cat}
                <span
                  className={`ml-1.5 font-body text-[10px] px-1 py-0.5 rounded-full ${
                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-stone-100 text-stone-400"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 font-body text-sm px-4 py-3 rounded-lg bg-red-50 text-red-600 border border-red-200">
            {error}
          </div>
        )}

        {/* Product Grid — Stale-While-Revalidate: keep old content during transition */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5 transition-opacity duration-300 ${isPending ? "opacity-60" : "opacity-100"}`}
        >
          {showSkeleton
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                >
                  <div className="w-full h-48 bg-stone-100 animate-pulse" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-stone-100 rounded animate-pulse w-3/4" />
                    <div className="h-3 bg-stone-100 rounded animate-pulse w-1/2" />
                    <div className="h-7 bg-stone-100 rounded animate-pulse w-full mt-2" />
                  </div>
                </div>
              ))
            : filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
        </div>

        {filteredProducts.length === 0 && !showSkeleton && (
          <div className="text-center py-16">
            <i className="ri-search-line text-4xl text-stone-300" />
            <p className="font-body text-sm text-stone-500 mt-3">
              未找到符合條件的貨源。
            </p>
            <button
              onClick={clearFilters}
              className="font-body text-xs text-rose-700 hover:text-rose-900 mt-2"
            >
              清除所有篩選
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}